<template>
  <tbody class="divide-y">
    <tr>
      <td
        :class="[
          active ? 'bg-gray-900 text-white' : 'bg-slate-100',
          'px-4 py-3 text-sm font-medium',
        ]"
        colspan="7"
      >
        <div class="flex items-center gap-3">
          <BaseToggle v-model="active" />
          <span class="text-base font-medium">{{ feature.display_name }}</span>
        </div>
      </td>
    </tr>
    <template v-if="active && hasOptions">
      <tr class="divide-x divide-slate-200 bg-slate-50">
        <th
          scope="col"
          colspan="7"
          class="px-3 py-2 text-left text-xs font-semibold text-slate-900"
        >
          OPTIONS
        </th>
      </tr>
      <tr class="divide-x divide-slate-200 bg-slate-50">
        <th scope="col" class="px-3 py-2 text-left text-xs font-semibold text-slate-900">NAME</th>
        <th scope="col" class="px-3 py-2 text-left text-xs font-semibold text-slate-900 w-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M5.428 6c6.018 0 8.414 10 13.143 10 2.114 0 3.48-1.578 3.48-4s-1.366-4-3.48-4c-1.665 0-2.861 1.069-4.017 2.395l-1.244-1.562c1.499-1.532 3.049-2.833 5.26-2.833 3.198 0 5.43 2.455 5.43 6s-2.232 6-5.43 6c-6.003 0-8.406-10-13.142-10-2.113 0-3.479 1.578-3.479 4s1.365 4 3.479 4c1.664 0 2.86-1.068 4.015-2.392l1.244 1.561c-1.499 1.531-3.05 2.831-5.259 2.831-3.197 0-5.428-2.455-5.428-6s2.231-6 5.428-6z"
            />
          </svg>
        </th>
        <th scope="col" class="px-3 py-2 text-right text-xs font-semibold text-slate-900 w-32">
          VALUE
        </th>
        <th scope="col" class="px-3 py-2 text-right text-xs font-semibold text-slate-900 w-32">
          PRICE / UNIT
        </th>
        <th scope="col" class="px-3 py-2 text-right text-xs font-semibold text-slate-900 w-32">
          FLAT PRICE
        </th>
        <th scope="col" class="px-3 py-2 text-right text-xs font-semibold text-slate-900 w-32">
          RESETTABLE PERIOD
        </th>
        <th></th>
      </tr>
      <ProductFeatureOptionManager
        v-for="item in internalFeatureOptionsValue"
        :key="item.feature_option_id"
        :label="getOptionLabelById(item.feature_option_id)"
        :price-presets="getPricePresetsById(item.feature_option_id)"
        :item="item"
        @change="handleItemChange"
      />
    </template>
    <template v-if="active && hasPermissionOptions">
      <tr class="divide-x divide-slate-200 bg-slate-50">
        <th
          scope="col"
          colspan="7"
          class="px-3 py-2 text-left text-xs font-semibold text-slate-900"
        >
          PERMISSIONS
        </th>
      </tr>
      <tr>
        <td colspan="7" class="px-4 py-2 text-sm font-medium">
          <BaseChipsBasic
            v-model="selectedPermissionOptions"
            multiple
            :options="permissionOptions"
          />
        </td>
      </tr>
    </template>
  </tbody>
</template>

<script>
import ProductFeatureOptionManager from './ProductFeatureOptionManager.vue';

export default {
  components: {
    ProductFeatureOptionManager,
  },
  props: {
    featureOptionsValue: {
      type: Array,
      default: () => [],
    },
    featurePermissionOptionsValue: {
      type: Array,
      default: () => [],
    },
    feature: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      internalFeatureOptionsValue: [],
      internalFeaturePermissionOptionsValue: [],
    };
  },
  computed: {
    active: {
      get() {
        return this.internalFeatureOptionsValue.length > 0;
      },
      set(value) {
        this.internalFeatureOptionsValue = [];
        this.internalFeaturePermissionOptionsValue = [];
        if (value && this.feature.options.length) {
          this.feature.options.forEach((option) => {
            this.internalFeatureOptionsValue.push({
              feature_id: this.feature.id,
              feature_option_id: option.id,
              value: -1,
              unit_amount: 0,
              flat_amount: 0,
              resettable_period: 'lifetime',
            });
          });
        }

        if (value && this.feature.permission_options.length) {
          this.feature.permission_options.forEach((permissionOption) => {
            this.internalFeaturePermissionOptionsValue.push({
              feature_id: this.feature.id,
              feature_permission_option_id: permissionOption.id,
            });
          });
        }

        this.updateFeatureOptionsValue();
        this.updateFeaturePermissionOptionsValue();
      },
    },
    hasOptions() {
      return this.feature.options.length > 0;
    },
    permissionOptions() {
      return (this.feature.permission_options || []).map((permissionOption) => ({
        value: permissionOption.id,
        label: permissionOption.permission?.name || 'Uknown',
      }));
    },
    hasPermissionOptions() {
      return this.permissionOptions.length > 0;
    },
    selectedPermissionOptions: {
      get() {
        return this.internalFeaturePermissionOptionsValue.map((option) => option.id);
      },
      set(value) {
        this.internalFeaturePermissionOptionsValue = this.feature.permission_options
          .filter((permissionOption) => value.includes(permissionOption.id))
          .map((permissionOption) => ({
            feature_id: this.feature.id,
            id: permissionOption.id,
            permission_id: permissionOption.permission_id,
          }));

        this.updateFeaturePermissionOptionsValue();
      },
    },
  },
  created() {
    this.internalFeatureOptionsValue = JSON.parse(
      JSON.stringify(this.featureOptionsValue.filter((item) => item.feature_id === this.feature.id))
    );
    this.internalFeaturePermissionOptionsValue = JSON.parse(
      JSON.stringify(
        this.featurePermissionOptionsValue.filter((item) => item.feature_id === this.feature.id)
      )
    );
  },
  methods: {
    getOptionLabelById(id) {
      return (
        this.feature.options.find((option) => option.id === id)?.display_name ||
        'Unknown option name'
      );
    },
    getPricePresetsById(id) {
      return this.feature.options.find((option) => option.id === id)?.prices || [];
    },
    handleItemChange(item) {
      const index = this.internalFeatureOptionsValue.findIndex(
        (i) => i.feature_option_id === item.feature_option_id
      );

      if (index === -1) {
        return;
      }

      this.internalFeatureOptionsValue.splice(index, 1, item);

      this.updateFeatureOptionsValue();
    },
    updateFeatureOptionsValue() {
      this.$emit(
        'update:feature-options-value',
        JSON.parse(
          JSON.stringify([
            ...this.featureOptionsValue.filter((item) => item.feature_id !== this.feature.id),
            ...this.internalFeatureOptionsValue,
          ])
        )
      );
    },
    updateFeaturePermissionOptionsValue() {
      this.$emit(
        'update:feature-permission-options-value',
        JSON.parse(
          JSON.stringify([
            ...this.featurePermissionOptionsValue.filter(
              (item) => item.feature_id !== this.feature.id
            ),
            ...this.internalFeaturePermissionOptionsValue,
          ])
        )
      );
    },
  },
};
</script>
